import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  CircularProgress,
  Button,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  LocalFireDepartment,
  Bolt,
  Home,
  TrendingUp,
  TrendingDown,
  EmojiEvents,
  Lock
} from '@mui/icons-material';
import Dashboard from '../components/Dashboard';
import Header from '../components/Header';
import TrendChart from '../components/TrendChart';
import { selectSelectedStudent } from '../store/selectors/studentSelectors';
import {
  fetchProgressSummary,
  fetchProgressTrend,
  Badge,
  ProgressSummary,
  ProgressTrend
} from '../services/progressService';

/**
 * Progress — streaks, XP and badges (#11) alongside the score trend (#12).
 *
 * They share a screen because they answer the same question from two sides: "am I getting
 * better?". Splitting them would give each half a page too thin to be worth opening.
 *
 * Both are free. Retention is the point of #11, and paywalling the evidence that the
 * product works would be an odd thing to sell.
 */

const StatTile: React.FC<{
  icon: React.ReactNode;
  value: string;
  label: string;
  accent?: string;
}> = ({ icon, value, label, accent }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Box sx={{ color: accent ?? 'primary.main', mb: 0.5 }}>{icon}</Box>
      <Typography variant="h4" sx={{ lineHeight: 1.1 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </CardContent>
  </Card>
);

const BadgeRow: React.FC<{ badge: Badge }> = ({ badge }) => {
  const pct =
    badge.threshold > 0
      ? Math.min(100, Math.round((badge.progress / badge.threshold) * 100))
      : 0;

  return (
    <Box sx={{ mb: 2, opacity: badge.earned ? 1 : 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        {badge.earned ? (
          <EmojiEvents fontSize="small" sx={{ color: '#a8791f' }} />
        ) : (
          <Lock fontSize="small" color="disabled" />
        )}
        <Typography variant="body2" sx={{ fontWeight: badge.earned ? 700 : 500, flex: 1 }}>
          {badge.title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {badge.earned ? 'Earned' : `${badge.progress} / ${badge.threshold}`}
        </Typography>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {badge.description}
      </Typography>
      {!badge.earned && (
        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
      )}
    </Box>
  );
};

const ProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedStudent = useSelector(selectSelectedStudent);

  const [summary, setSummary] = useState<ProgressSummary | null>(null);
  const [trend, setTrend] = useState<ProgressTrend | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedStudent) {
      navigate('/select-student');
    }
  }, [selectedStudent, navigate]);

  useEffect(() => {
    let cancelled = false;
    const studentId = selectedStudent?.id;
    if (!studentId) return undefined;

    setLoading(true);
    Promise.all([fetchProgressSummary(studentId), fetchProgressTrend(studentId)]).then(
      ([s, t]) => {
        if (cancelled) return;
        setSummary(s);
        setTrend(t);
        setLoading(false);
      }
    );

    return () => {
      cancelled = true;
    };
  }, [selectedStudent?.id]);

  if (!selectedStudent) return null;

  const improving = (trend?.improvementPct ?? 0) >= 0;
  const plottable = (trend?.points ?? []).filter((p) => p.percentage != null);

  const body = loading ? (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  ) : (
    <Box sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Your progress
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Everything here is worked out from the questions you have already answered.
      </Typography>

      {/* ── Streak, XP, accuracy (#11) ─────────────────────────────────────── */}
      {summary && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile
                icon={<LocalFireDepartment />}
                accent="#e2622a"
                value={`${summary.currentStreakDays}`}
                label="day streak"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile icon={<Bolt />} value={`${summary.xp}`} label="XP earned" />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile
                icon={<EmojiEvents />}
                accent="#a8791f"
                value={
                  summary.accuracyPct != null ? `${summary.accuracyPct.toFixed(0)}%` : '—'
                }
                label="accuracy"
              />
            </Grid>
            <Grid size={{ xs: 6, md: 3 }}>
              <StatTile
                icon={<TrendingUp />}
                value={`${summary.questionsAttempted}`}
                label="questions answered"
              />
            </Grid>
          </Grid>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Badges
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {summary.nextBadge
                ? `Next up: ${summary.nextBadge.title} — ${summary.nextBadge.description}.`
                : 'Every badge earned. '}
              {summary.longestStreakDays > summary.currentStreakDays &&
                ` Your best streak was ${summary.longestStreakDays} days.`}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {summary.badges?.length ? (
              summary.badges.map((b) => <BadgeRow key={b.key} badge={b} />)
            ) : (
              <Typography variant="body2" color="text.secondary">
                Answer a few questions to start earning badges.
              </Typography>
            )}
          </Paper>
        </>
      )}

      {/* ── Trend (#12) ────────────────────────────────────────────────────── */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {improving ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
          <Typography variant="h6">Improvement over time</Typography>
        </Box>

        {plottable.length < 2 ? (
          <Typography variant="body2" color="text.secondary">
            {plottable.length === 0
              ? 'No completed papers yet. Your scores will chart here once you have sat one.'
              : 'One paper so far. A second attempt is what turns a score into a trend.'}
          </Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {trend?.callout ??
                (trend?.improvementPct != null
                  ? `${improving ? 'Up' : 'Down'} ${Math.abs(trend.improvementPct).toFixed(
                      1
                    )} percentage points across ${plottable.length} attempts.`
                  : `${plottable.length} attempts.`)}
            </Typography>

            <TrendChart points={trend!.points} />

            {trend?.bySubject && trend.bySubject.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  By subject
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Subject</TableCell>
                      <TableCell align="right">Attempts</TableCell>
                      <TableCell align="right">First</TableCell>
                      <TableCell align="right">Latest</TableCell>
                      <TableCell align="right">Change</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trend.bySubject.map((s) => (
                      <TableRow key={s.subject}>
                        <TableCell>{s.subject}</TableCell>
                        <TableCell align="right">{s.attempts}</TableCell>
                        <TableCell align="right">
                          {s.firstPercentage != null ? `${s.firstPercentage.toFixed(1)}%` : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {s.latestPercentage != null
                            ? `${s.latestPercentage.toFixed(1)}%`
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {s.deltaPct == null ? (
                            // One attempt in this subject — no change to report.
                            <Typography variant="caption" color="text.secondary">
                              —
                            </Typography>
                          ) : (
                            <Chip
                              size="small"
                              color={s.deltaPct >= 0 ? 'success' : 'error'}
                              variant="outlined"
                              label={`${s.deltaPct >= 0 ? '+' : ''}${s.deltaPct.toFixed(1)}`}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button variant="contained" startIcon={<Home />} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dashboard
      constrained
      header={<Header selectedStudent={selectedStudent} />}
      students={body}
    />
  );
};

export default ProgressPage;
